import { useCallback, useEffect, useMemo, useState } from 'react';

import { exportRegister } from './register-export.js';
import {
  ApiError,
  createTask,
  listTasks,
  resolveTaskBlocker,
  setTaskWatching,
  updateTask,
  type Task,
  type TaskStatus,
} from '../api.js';
import { formatDate } from '../labels.js';
import { AppLink } from '../link.js';
import { navigate } from '../router.js';
import { Attachments } from './Attachments.js';
import { Comments } from './Comments.js';
import { useArrivedToCreate } from './cross-links.js';
import { Dependencies } from './Dependencies.js';
// No `sectionHref` here: a task is never deleted — it is cancelled, and it stays
// on its own page saying so — so there is no move that leaves the reader on an
// address that names nothing.
import { recordHref } from './project-sections.js';
import { RecordBack, RecordMissing, RecordPage } from './record-page.js';
import {
  Drawer,
  RegisterToolbar,
  RowActions,
  matches,
  useRegisterView,
  type RowAction,
} from './register-kit.js';
import { LOAD_ALL_LIMIT } from '../api/paging.js';
import { BlockerForm, TaskForm } from './TaskForms.js';
import { TaskBoard, TaskCalendar } from './TaskViews.js';
import { CAUSE_LABELS, MOVE_LABELS, PRIORITY_LABELS, STATUS_LABELS } from './task-labels.js';

/**
 * The tasks on a project.
 *
 * Ordering comes from the SERVER — overdue first, then due soonest, then
 * hardest — because a list ordered by when things were typed in is a list
 * nobody can work from. The screen does not re-sort it.
 *
 * Which buttons appear also comes from the server (`allowed_next`). Some moves
 * belong to the state machine and some to the caller's role; neither is
 * re-decided here.
 *
 * ON THE REGISTER PATTERN — three things this screen does DIFFERENTLY
 * -------------------------------------------------------------------
 * It is the one register the pattern could not be copied onto, and the
 * differences are all deliberate:
 *
 *   1. **It stays a list, not a table.** A task carries a title, a
 *      description, a priority chip and a list of blockers with their ages.
 *      None of those are columns, and forcing them into some would make the
 *      screen worse to answer the question "is it consistent".
 *   2. **There is no sort control.** The server's order is the feature — see
 *      above — and a sort box beside it would invite somebody to throw it away.
 *      Search and density are client-side and take nothing away.
 *   3. **Open / Mine / All stay their own control**, because they change what
 *      is FETCHED. As a toolbar filter the count line would compare a filtered
 *      list against itself, which is the trap `Activities.tsx` documents.
 *
 * What the pattern did bring: the row had up to nine controls on it and three
 * panels that opened inside it — comments, dependencies and photos. Those are
 * on the record now, and the row has one button and a menu.
 */

type Filter = 'open' | 'all' | 'mine';

interface Props {
  readonly projectId: string;
  /**
   * One task, as its own page, from `/projects/<id>/site/tasks/<recordId>`.
   *
   * A task EARNS a page, and this screen is half the reason the rule exists. Its
   * conversation, what it waits on and the photos of the work used to open
   * INSIDE the row, pushing every task below them down the page — on the screen
   * people spend the most time in. The drawer stopped that; a page lets all
   * three be read without any of them being a scroll.
   *
   * **It also forces the fetch to `all`, and that is not cosmetic.** Open / Mine
   * / All change what is FETCHED here (§4.1c) and the default is Open, so a link
   * to a finished task would have found nothing and said the task did not
   * exist. See the filter state below.
   */
  readonly recordId?: string | null;
  readonly currentUserId: string;
  /**
   * `task: X` in `04`.
   *
   * Every other register gated its Export button when it gained one. This
   * screen was copied from one that did and arrived without it, and no
   * capability was ever added — so a subcontractor, who holds view and edit
   * on their OWN tasks and no export right at all, was offered a CSV of the
   * register. The server refuses it now whatever the screen does; this is so
   * the button is not offered in the first place.
   */
  readonly canExport: boolean;
  /**
   * `task: C`. The same fault as the export, found from the other side: the
   * owner signed in as a subcontractor (`VE(assigned)`, no `C`) on 2026-09-04
   * and was offered "Add task". The server had refused it all along.
   */
  readonly canAdd: boolean;
}

type State =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly tasks: readonly Task[];
      /**
       * How many match the SAME filters on the server, which is not the same as
       * how many are loaded. See `api/paging.ts` — the search box here works
       * over what is loaded, so the difference has to be shown, not hidden.
       */
      readonly registerTotal: number;
    }
  | { readonly status: 'not_permitted' }
  | { readonly status: 'failed'; readonly message: string };

/**
 * What the drawer is doing. `null` means it is shut.
 *
 * `detail` is gone: the task has a page and an address now (§4.3). The forms
 * stay, and `block` above all — `05` requires a cause and a description, and the
 * status move and the blocker are written in ONE transaction.
 */
type DrawerMode =
  | { readonly kind: 'edit'; readonly id: string }
  | { readonly kind: 'block'; readonly id: string }
  | { readonly kind: 'add' };

/**
 * The lifecycle forwards, for picking the row's one action.
 *
 * `blocked` and `cancelled` are absent. Blocking demands a cause and a
 * description — the server refuses without them — and cancelling is an ending.
 * Neither belongs one click away.
 */
const FORWARD: readonly TaskStatus[] = ['in_progress', 'done', 'todo'];

export function Tasks({
  projectId,
  recordId = null,
  currentUserId,
  canExport,
  canAdd,
}: Props): JSX.Element {
  const [state, setState] = useState<State>({ status: 'loading' });
  /*
   * `all` ON A RECORD PAGE, and it is load-bearing rather than tidy.
   *
   * This is the one register whose default filter changes what is FETCHED, not
   * just what is shown. Left on `open`, a link to a finished or cancelled task
   * would fetch a list that does not contain it and the page would say "that
   * task is not here" — which is the `?focus=` failure the record page exists
   * to fix, reproduced exactly.
   *
   * The chips are not on a record page anyway: they live in the register's own
   * head, which a record page replaces with the link back.
   */
  const [filter, setFilter] = useState<Filter>(recordId ? 'all' : 'open');
  /** Whether this screen has asked the server for the whole board. */
  const [wantAll, setWantAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState<DrawerMode | null>(null);
  /** Set by the form inside the drawer, so closing it can ask before discarding. */
  const [formDirty, setFormDirty] = useState(false);
  /**
   * List, board or calendar — three arrangements of the SAME rows, same
   * fetch, same permissions. The board answers "where is everything", the
   * calendar answers "what lands when"; neither invents a second data path.
   */
  const [arrangement, setArrangement] = useState<'list' | 'board' | 'calendar'>('list');
  // No default sort: the server's order is the one that matters. See above.
  const view = useRegisterView('server');

  const load = useCallback(
    async (which: Filter) => {
      try {
        const page = await listTasks(
          projectId,
          {
            status: which === 'all' ? 'all' : 'open',
            ...(which === 'mine' ? { owner_id: currentUserId } : {}),
          },
          wantAll ? LOAD_ALL_LIMIT : undefined,
        );
        setState({ status: 'ready', tasks: page.rows, registerTotal: page.total });
      } catch (caught) {
        const denied =
          caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
        setState(
          denied
            ? { status: 'not_permitted' }
            : { status: 'failed', message: 'Could not load the tasks.' },
        );
      }
    },
    [projectId, currentUserId, wantAll],
  );

  useEffect(() => {
    void load(filter);
  }, [load, filter]);

  async function run(work: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await work();
      // Re-read rather than patching the list in place: a change can reorder it
      // (a due date, a priority) or remove a row from it (finishing something
      // while the filter is "open"), and guessing which would be wrong.
      await load(filter);
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Shut the drawer, and forget whether its form had edits in it. */
  const closeDrawer = useCallback(() => {
    setDrawer(null);
    setFormDirty(false);
    setError(null);
  }, []);

  /** `?new=task` — arrived from the site home to write one. */
  const startNew = useCallback(() => setDrawer({ kind: 'add' }), []);
  useArrivedToCreate('task', startNew);

  const tasks = state.status === 'ready' ? state.tasks : null;

  /**
   * The rows on screen — SEARCH ONLY, and the server's order kept.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps`. It runs before the early
   * returns below because hooks must.
   */
  const shown = useMemo(() => {
    if (!tasks) return [];
    return tasks.filter((task) =>
      matches(
        [task.title, task.description, task.owner_name, task.location_path]
          .filter(Boolean)
          .join(' '),
        view.query,
      ),
    );
  }, [tasks, view.query]);

  /** The record the drawer is about. `null` for the add form, which has none. */
  const subject =
    drawer && drawer.kind !== 'add' ? (tasks?.find((t) => t.id === drawer.id) ?? null) : null;

  /** Make a move, asking for the cause first where the server demands one. */
  function move(task: Task, to: TaskStatus): void {
    setError(null);
    // Blocking asks WHY first — the server refuses without it, so the form is
    // the honest way to ask.
    if (to === 'blocked') {
      setDrawer({ kind: 'block', id: task.id });
      return;
    }
    void run(() => updateTask(projectId, task.id, { status: to }));
  }

  /** The one move that goes on the row. */
  function primaryFor(task: Task): RowAction {
    const forward = FORWARD.find((status) => task.allowed_next.includes(status));
    if (forward) {
      return {
        label: MOVE_LABELS[forward],
        disabled: busy,
        onSelect: () => move(task, forward),
      };
    }
    return {
      label: 'Open',
      onSelect: () => navigate(recordHref(projectId, 'tasks', task.id)),
    };
  }

  /**
   * Everything that is not the one visible action.
   *
   * The same list serves the row and the record page. "Open" comes off on the
   * page, for the same reason it always does: you are already looking at it.
   */
  function overflowFor(
    task: Task,
    primary: RowAction | null,
    onPage = false,
  ): readonly RowAction[] {
    const actions: RowAction[] = onPage
      ? []
      : [{ label: 'Open', onSelect: () => navigate(recordHref(projectId, 'tasks', task.id)) }];
    // The register never offers a move the machine has not returned.
    for (const to of task.allowed_next) {
      actions.push({
        label: MOVE_LABELS[to],
        danger: to === 'cancelled',
        disabled: busy,
        onSelect: () => move(task, to),
      });
    }
    if (task.allowed_next.length > 0) {
      actions.push({ label: 'Edit', onSelect: () => setDrawer({ kind: 'edit', id: task.id }) });
    }
    // A subscription, not a role — anybody who can see the task may follow it,
    // and the label says which state you are in.
    actions.push({
      label: task.watching ? 'Watching' : 'Watch',
      disabled: busy,
      onSelect: () => void run(() => setTaskWatching(projectId, task.id, !task.watching)),
    });
    // Whatever is already on the row must not also be in the menu behind it.
    return primary ? actions.filter((action) => action.label !== primary.label) : actions;
  }

  async function exportRows(): Promise<void> {
    const headers = [
      'Task',
      'Status',
      'Priority',
      'Owner',
      'Where',
      'Due',
      'Overdue',
      'Blocked because',
      'Description',
    ];
    const rows = shown.map((task) => [
      task.title,
      STATUS_LABELS[task.status],
      PRIORITY_LABELS[task.priority],
      task.owner_name ?? '',
      task.location_path ?? '',
      task.due_date ?? '',
      task.overdue ? 'Yes' : '',
      task.blockers
        .map((b) => `${CAUSE_LABELS[b.cause_type]} — ${b.description}`)
        .join('; '),
      task.description ?? '',
    ]);
    setError(null);
    try {
      // Recorded BEFORE the file is written, and refusable — `09` §Threats
      // wants the row count and the filters. See `register-export.ts`.
      await exportRegister({
        projectId,
        resource: 'task',
        filename: `tasks-${projectId.slice(0, 8)}.csv`,
        headers,
        rows,
        filters: { query: view.query, ...view.chosen },
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not export.');
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Tasks</h3>
      {state.status === 'ready' && (
        <div className="head-actions">
          {/*
            Open / Mine / All change what is FETCHED, so they are not toolbar
            filters — see the note at the top of the file.
          */}
          <div className="filter-group" role="group" aria-label="Which tasks to show">
            {(['open', 'mine', 'all'] as const).map((key) => (
              <button
                key={key}
                type="button"
                className={filter === key ? 'chip-button current' : 'chip-button'}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
              >
                {key === 'open' ? 'Open' : key === 'mine' ? 'Mine' : 'All'}
              </button>
            ))}
          </div>
          <div className="filter-group" role="group" aria-label="How to arrange them">
            {(['list', 'board', 'calendar'] as const).map((key) => (
              <button
                key={key}
                type="button"
                className={arrangement === key ? 'chip-button current' : 'chip-button'}
                aria-pressed={arrangement === key}
                onClick={() => setArrangement(key)}
              >
                {key === 'list' ? 'List' : key === 'board' ? 'Board' : 'Calendar'}
              </button>
            ))}
          </div>
          {canAdd && (
            <button type="button" className="inline" onClick={() => setDrawer({ kind: 'add' })}>
              Add task
            </button>
          )}
        </div>
      )}
    </div>
  );

  /*
   * The same four states on a record page, still four different sentences. The
   * furniture above them changes: the register's head carries Open/Mine/All and
   * a "Add task" button, and neither is the right thing to offer somebody who
   * followed a link to one particular task.
   */
  const above = recordId ? <RecordBack projectId={projectId} register="tasks" /> : head;

  if (state.status === 'loading') {
    return (
      <>
        {above}
        <p aria-live="polite">Loading {recordId ? 'the task' : 'tasks'}…</p>
      </>
    );
  }

  if (state.status === 'not_permitted') {
    return (
      <>
        {above}
        <p className="hint">Your role does not include this project&apos;s tasks.</p>
      </>
    );
  }

  if (state.status === 'failed') {
    return (
      <>
        {above}
        <div className="alert" role="alert">
          <span>{state.message}</span>
        </div>
      </>
    );
  }

  /**
   * The three forms, shared by the register and the record page.
   *
   * `BlockerForm` above all: `05` requires a cause and a description, and the
   * status move and the blocker are written in ONE transaction — a refresh in
   * between would show a blocked task with no explanation. A page with its own
   * copy is a second place for that to come apart.
   */
  const drawers = (
    <>
      {drawer?.kind === 'add' && (
        <Drawer title="Add a task" dirty={formDirty} onClose={closeDrawer}>
          <TaskForm
            projectId={projectId}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => createTask(projectId, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'edit' && subject && (
        <Drawer title={`Edit ${subject.title}`} dirty={formDirty} onClose={closeDrawer}>
          <TaskForm
            projectId={projectId}
            task={subject}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              // The version this form was OPENED on. Without it, a stale copy
              // of the owner silently REASSIGNS the task to whoever it named
              // when the form was opened. The status buttons on the row do NOT
              // send one: the state machine already refuses a move from a
              // status the task is no longer in.
              if (await run(() => updateTask(projectId, subject.id, input, subject.record_version)))
                closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'block' && subject && (
        <Drawer
          title={`Block ${subject.title}`}
          subtitle="A blocked task needs a cause and a description"
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <BlockerForm
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (blocker) => {
              if (
                await run(() => updateTask(projectId, subject.id, { status: 'blocked', blocker }))
              ) {
                closeDrawer();
              }
            }}
          />
        </Drawer>
      )}
    </>
  );

  const alert = error && (
    <div className="alert" role="alert">
      <span>{error}</span>
    </div>
  );

  // ---------------------------------------------------------- one task, in full
  if (recordId) {
    const record = state.tasks.find((t) => t.id === recordId) ?? null;
    if (!record) {
      /*
       * A FAILED LOAD IS NOT A MISSING RECORD — but here the `failed` state has
       * already returned above, so reaching this with an empty list genuinely
       * means the id names nothing. The fetch is forced to `all` (see the filter
       * state), so a finished task is in the list rather than filtered out of it.
       */
      return <RecordMissing projectId={projectId} register="tasks" />;
    }

    const forward = primaryFor(record);
    const primary = forward.label === 'Open' ? null : forward;
    /* NO BAR WHEN THERE IS NOTHING IN IT — see `Drawings.tsx`. */
    const more = overflowFor(record, primary, true);
    const actions =
      primary || more.length > 0 ? (
        <RowActions rowLabel={record.title} {...(primary ? { primary } : {})} more={more} />
      ) : null;

    return (
      <>
        {alert}
        {/*
          NO `reference`. A task is named by its title and carries no code, and
          putting a typed sentence in the mono face would say "this is a
          reference" about something that is not one.
        */}
        <RecordPage
          projectId={projectId}
          register="tasks"
          title={record.title}
          status={STATUS_LABELS[record.status]}
          meta={
            <>
              <span className={`chip chip-priority-${record.priority}`}>
                {PRIORITY_LABELS[record.priority]}
              </span>
              {record.owner_name && <> · {record.owner_name}</>}
              {record.location_path && <> · {record.location_path}</>}
              {record.due_date && (
                <span className={record.overdue ? 'task-overdue' : undefined}>
                  {' · '}
                  {record.overdue ? 'Overdue — was due' : 'Due'} {formatDate(record.due_date)}
                </span>
              )}
            </>
          }
          {...(actions ? { actions } : {})}
          properties={<TaskProperties task={record} />}
        >
          {/*
            WHY IT IS STOPPED COMES FIRST. A blocked task whose explanation is
            hidden is a status and not an explanation — the register puts the
            blockers on the row itself for that reason, and the page keeps them
            at the top rather than below the conversation.
          */}
          {record.blockers.length > 0 && (
            <div className="register record-section">
              <div className="section-head sub-head-row">
                <h4 className="sub-head">What is stopping it</h4>
              </div>
              <ul className="blocker-list">
                {record.blockers.map((blocker) => (
                  <li key={blocker.id} className={`blocker sev-${blocker.severity}`}>
                    <span className="blocker-why">
                      <strong>{CAUSE_LABELS[blocker.cause_type]}</strong> — {blocker.description}
                    </span>
                    <span className="blocker-age">
                      {blocker.days_open === 0
                        ? 'Raised today'
                        : `Open ${blocker.days_open} day${blocker.days_open === 1 ? '' : 's'}`}
                      {blocker.opened_by_name && ` by ${blocker.opened_by_name}`}
                    </span>
                    <button
                      type="button"
                      className="link-button"
                      disabled={busy}
                      onClick={() =>
                        void run(() => resolveTaskBlocker(projectId, record.id, blocker.id))
                      }
                    >
                      Resolved
                      <span className="visually-hidden"> — {blocker.description}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Conversation</h4>
            </div>
            <Comments
              projectId={projectId}
              taskId={record.id}
              // The count on the row changes with the conversation, so the list
              // is re-read rather than left stale.
              onCountChanged={() => void load(filter)}
            />
          </div>

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">What it waits on</h4>
            </div>
            {/*
              `06` P4 — the same one dependency model activities use, not a
              second one.
            */}
            <Dependencies
              projectId={projectId}
              entityType="task"
              entityId={record.id}
              candidates={state.tasks.map((t) => ({ id: t.id, label: t.title }))}
              canEdit={record.allowed_next.length > 0}
              // A new predecessor may block this task at once (rule P4), so the
              // list re-reads itself.
              onChanged={() => void load(filter)}
            />
          </div>

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Photos</h4>
            </div>
            {/* W3 makes capture-against-a-task a core site flow. Anybody who can
                move the task can evidence it. */}
            <Attachments
              projectId={projectId}
              entityType="task"
              entityId={record.id}
              can={{ write: record.allowed_next.length > 0 }}
              noun="photos"
            />
          </div>
        </RecordPage>
        {drawers}
      </>
    );
  }

  return (
    <>
      {head}

      {alert}

      {state.tasks.length === 0 ? (
        <div className="empty">
          <p>
            <strong>
              {filter === 'mine'
                ? 'Nothing assigned to you.'
                : filter === 'all'
                  ? 'No tasks yet.'
                  : 'No open tasks.'}
            </strong>
          </p>
          {filter === 'open' && <p>Anything finished is under “All”.</p>}
        </div>
      ) : arrangement === 'board' ? (
        <TaskBoard
          tasks={state.tasks}
          busy={busy}
          onMove={(taskId, to) => {
            if (to === 'blocked') {
              setError(null);
              setDrawer({ kind: 'block', id: taskId });
              return;
            }
            void run(() => updateTask(projectId, taskId, { status: to }));
          }}
        />
      ) : arrangement === 'calendar' ? (
        <TaskCalendar tasks={state.tasks} />
      ) : (
        <>
          <RegisterToolbar
            view={view}
            searchLabel="Search tasks"
            searchPlaceholder="What it is, who has it, where"
            shown={shown.length}
            total={state.tasks.length}
            registerTotal={state.registerTotal}
            onLoadAll={() => setWantAll(true)}
            loadingAll={wantAll && state.tasks.length < state.registerTotal}
            noun={['task', 'tasks']}
          >
            {canExport && (
              <button
                type="button"
                className="secondary inline"
                onClick={() => void exportRows()}
              >
                Export {shown.length === state.tasks.length ? 'all' : 'these'} to CSV
              </button>
            )}
          </RegisterToolbar>

          {shown.length === 0 ? (
            /*
             * A DIFFERENT empty state from "no open tasks": this reader has a
             * list in front of them and a search that matches none of it.
             */
            <div className="empty">
              <p>
                <strong>No task matches that.</strong>
              </p>
              <p>
                <button type="button" className="link-button" onClick={view.clear}>
                  Clear the search
                </button>{' '}
                to see all {state.tasks.length}.
              </p>
            </div>
          ) : (
            <ul className={`task-list${view.density === 'compact' ? ' rk-compact' : ''}`}>
              {shown.map((task) => {
                const primary = primaryFor(task);
                return (
                  <li key={task.id} className={`task-row task-${task.status}`}>
                    <div className="task-main">
                      {/*
                        A REAL LINK, because the record has an address now — so
                        a middle-click, a new tab and a copied link all mean what
                        they look like they mean.
                      */}
                      <AppLink
                        className="rk-open task-title"
                        to={recordHref(projectId, 'tasks', task.id)}
                      >
                        {task.title}
                        <span className="visually-hidden"> — open this task</span>
                      </AppLink>
                      <span className="task-meta">
                        <span className={`chip chip-priority-${task.priority}`}>
                          {PRIORITY_LABELS[task.priority]}
                        </span>
                        <span>{STATUS_LABELS[task.status]}</span>
                        {task.owner_name && <span>· {task.owner_name}</span>}
                        {task.location_path && <span>· {task.location_path}</span>}
                        {task.due_date && (
                          <span className={task.overdue ? 'task-overdue' : undefined}>
                            · {task.overdue ? 'Overdue — was due' : 'Due'}{' '}
                            {formatDate(task.due_date)}
                          </span>
                        )}
                      </span>
                      {task.description && (
                        <span className="task-description rk-secondary">{task.description}</span>
                      )}
                    </div>

                    {/* The reason lives with the task, not behind a click. A
                        blocked task whose explanation is hidden is a status. */}
                    {task.blockers.length > 0 && (
                      <ul className="blocker-list">
                        {task.blockers.map((blocker) => (
                          <li key={blocker.id} className={`blocker sev-${blocker.severity}`}>
                            <span className="blocker-why">
                              <strong>{CAUSE_LABELS[blocker.cause_type]}</strong> —{' '}
                              {blocker.description}
                            </span>
                            <span className="blocker-age">
                              {blocker.days_open === 0
                                ? 'Raised today'
                                : `Open ${blocker.days_open} day${blocker.days_open === 1 ? '' : 's'}`}
                              {blocker.opened_by_name && ` by ${blocker.opened_by_name}`}
                            </span>
                            <button
                              type="button"
                              className="link-button"
                              disabled={busy}
                              onClick={() =>
                                void run(() => resolveTaskBlocker(projectId, task.id, blocker.id))
                              }
                            >
                              Resolved
                              <span className="visually-hidden"> — {blocker.description}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    <span className="row-actions">
                      <RowActions
                        rowLabel={task.title}
                        primary={primary}
                        more={overflowFor(task, primary)}
                      />
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {drawers}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * What the task IS — the record page's rail.
 *
 * The three LISTS — the conversation, what it waits on and the photos of the
 * work — are in the main column. All three used to open INSIDE the row, which
 * pushed every task below them down the page, on the screen people spend the
 * most time in. The drawer stopped that by stacking them; the page is the first
 * arrangement where all three are readable without one being a scroll.
 */
function TaskProperties({ task }: { readonly task: Task }): JSX.Element {
  return (
    <>
      <dl className="detail-grid">
        <dt>Status</dt>
        <dd>{STATUS_LABELS[task.status]}</dd>
        <dt>Priority</dt>
        <dd>{PRIORITY_LABELS[task.priority]}</dd>
        <dt>Owner</dt>
        <dd>{task.owner_name ?? '—'}</dd>
        <dt>Due</dt>
        <dd>
          {formatDate(task.due_date)}
          {task.overdue && <span className="task-overdue"> · overdue</span>}
        </dd>
        {task.location_path && (
          <>
            <dt>Where</dt>
            <dd>{task.location_path}</dd>
          </>
        )}
        {task.description && (
          <>
            <dt>What it is</dt>
            <dd>{task.description}</dd>
          </>
        )}
      </dl>
    </>
  );
}
